import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { AdminRole } from "../api/types.js";
import { utc } from "../format.js";
import { Blank, Button, ErrorNote, Panel, Pill, ScreenHeader, ShortId, Spinner, Table, Td, Th } from "../ui.js";

/**
 * Withdrawals (docs/product/wallet-accounts.md): the ones waiting for a check, and the rest by
 * status. Approve pays it out on the next worker pass; reject returns the full amount and the fee
 * to the customer's balance. Both are `editor` actions on the existing admin write right.
 */

const STATUSES = ["awaiting_approval", "approved", "sent", "confirmed", "rejected", "failed"] as const;
type Status = (typeof STATUSES)[number];

const REASONS: Record<string, string> = {
  daily_over_limit: "over the 24 h limit for this account",
  fuse: "platform daily fuse",
};

const TONES: Record<Status, "good" | "attention" | "bad" | "moving" | "neutral"> = {
  awaiting_approval: "attention",
  approved: "moving",
  sent: "moving",
  confirmed: "good",
  rejected: "bad",
  failed: "bad",
};

const trx = (sun: number): string => (sun / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 6 });

export function WithdrawalsScreen({ role }: { role: AdminRole }) {
  const [status, setStatus] = useState<Status>("awaiting_approval");
  const [busy, setBusy] = useState<string | null>(null);
  const list = useResource(
    (signal) => {
      void signal;
      return adminReads.withdrawals(status);
    },
    [status],
  );
  const rows = list.data?.data ?? [];

  const act = (id: string, action: "approve" | "reject") => {
    setBusy(id);
    const call = action === "approve" ? adminWrites.approveWithdrawal(id) : adminWrites.rejectWithdrawal(id);
    void call.then(() => list.reload()).finally(() => setBusy(null));
  };

  return (
    <>
      <ScreenHeader
        title="Withdrawals"
        lead="Customer withdrawals back to the wallets that topped the accounts up. Approve pays it out; reject returns amount and fee to the balance."
      />
      <Panel>
        <div class="flex flex-wrap gap-2" role="group" aria-label="Status">
          {STATUSES.map((value) => (
            <Button key={value} tone={value === status ? "primary" : "ghost"} onClick={() => setStatus(value)}>
              {value.replace("_", " ")}
            </Button>
          ))}
        </div>
      </Panel>
      <div class="mt-3">
        <Panel title={`Withdrawals: ${status.replace("_", " ")} (${rows.length})`} subtitle="Oldest first. Times are UTC.">
          {list.loading ? (
            <Spinner />
          ) : list.error !== null ? (
            <ErrorNote error={list.error} onRetry={() => list.reload()} />
          ) : (
            <Table
              rows={rows.length}
              empty="Nothing here."
              head={
                <tr>
                  <Th>Requested</Th>
                  <Th>Account</Th>
                  <Th>To</Th>
                  <Th>Amount, TRX</Th>
                  <Th>Why it waits</Th>
                  <Th>Status</Th>
                  <Th>Transaction</Th>
                  <Th>{""}</Th>
                </tr>
              }
            >
              {rows.map((row) => (
                <tr key={row.id}>
                  <Td mono>{utc(row.created_at)}</Td>
                  <Td mono>
                    <ShortId value={row.account_id} />
                  </Td>
                  <Td mono>
                    <ShortId value={row.to_address} />
                  </Td>
                  <Td mono>{trx(row.amount_sun)}</Td>
                  <Td>{row.approval_reason === null ? <Blank /> : (REASONS[row.approval_reason] ?? row.approval_reason)}</Td>
                  <Td>
                    <Pill value={row.status} tone={TONES[row.status as Status] ?? "neutral"} />
                  </Td>
                  <Td mono>{row.txid === null ? <Blank /> : <ShortId value={row.txid} />}</Td>
                  <Td>
                    {row.status === "awaiting_approval" && role === "editor" ? (
                      <div class="flex gap-2">
                        <Button tone="primary" disabled={busy === row.id} onClick={() => act(row.id, "approve")}>
                          Approve
                        </Button>
                        <Button tone="ghost" disabled={busy === row.id} onClick={() => act(row.id, "reject")}>
                          Reject
                        </Button>
                      </div>
                    ) : null}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      </div>
    </>
  );
}
